import { useState, useEffect } from 'react'
import { CheckCircle, Printer, ArrowRight } from 'lucide-react'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'

interface CartItem {
  id: string
  name: string
  price: number
  quantity: number
  total: number
  unit?: string
}

interface Customer {
  id: string
  name: string
  phone?: string
}

interface SaleCompletionOverlayProps {
  isVisible: boolean
  saleData: {
    billNumber: string
    items: CartItem[]
    subtotal: number
    taxAmount: number
    totalAmount: number
    paymentMethod: string
    amountReceived: number
    change: number
    customer?: Customer | null
  }
  onPrint: () => Promise<void>
  onSkipPrint: () => void
  onNewSale: () => void
  formatCurrency: (amount: number) => string
}

export const SaleCompletionOverlay = ({
  isVisible,
  saleData,
  onPrint,
  onSkipPrint,
  onNewSale,
  formatCurrency
}: SaleCompletionOverlayProps) => {
  const [isPrinting, setIsPrinting] = useState(false)
  const [autoTimer, setAutoTimer] = useState(5)

  // Auto-timer effect for new sale
  useEffect(() => {
    if (!isVisible) return

    const timer = setInterval(() => {
      setAutoTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          onNewSale()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isVisible, onNewSale])

  // Reset timer when component becomes visible
  useEffect(() => {
    if (isVisible) {
      setAutoTimer(5)
      setIsPrinting(false)
    }
  }, [isVisible])

  if (!isVisible) return null

  const handlePrint = async () => {
    try {
      setIsPrinting(true)
      await onPrint()
      // After printing, start new sale immediately
      onNewSale()
    } catch (error) {
      console.error('Print failed:', error)
      setIsPrinting(false)
    }
  }

  const handleNewSaleNow = () => {
    onNewSale()
  }

  // Single step with auto-timer and two clear actions
  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-800 z-50 flex flex-col">
      {/* Compact Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Sale Completed
          </h3>
          <Badge variant="success">#{saleData.billNumber}</Badge>
        </div>
      </div>

      {/* Compact Sale Summary */}
      <div className="flex-1 p-4 flex flex-col justify-center">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          
          <h4 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {formatCurrency(saleData.totalAmount)}
          </h4>
          
          {saleData.customer && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              {saleData.customer.name}
            </p>
          )}
          
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {saleData.items.length} items • {saleData.paymentMethod}
          </p>
          
          {/* Compact Items List */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
            <div className="space-y-1">
              {saleData.items.map((item) => (
                <div key={item.id} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-300 truncate flex-1 text-left">
                    {item.name} ({item.quantity})
                  </span>
                  <span className="text-gray-900 dark:text-gray-100 font-medium ml-2">
                    {formatCurrency(item.total)}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {saleData.change > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Change: <span className="font-semibold">{formatCurrency(saleData.change)}</span>
              </p>
            </div>
          )}

          {/* Auto-timer indicator */}
          <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg mb-4">
            <p className="text-sm text-orange-600 dark:text-orange-400 mb-2">
              Starting new sale automatically in:
            </p>
            <div className="flex items-center justify-center gap-2">
              <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-orange-600 dark:text-orange-400">
                  {autoTimer}
                </span>
              </div>
              <span className="text-xs text-orange-500 dark:text-orange-400">seconds</span>
            </div>
          </div>
        </div>

        {/* Two Clear Action Buttons */}
        <div className="space-y-2">
          <Button
            onClick={handlePrint}
            disabled={isPrinting}
            size="lg"
            className="w-full flex items-center justify-center gap-2"
          >
            {isPrinting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Printing & Starting New Sale...
              </>
            ) : (
              <>
                <Printer className="w-4 h-4" />
                Print Receipt & New Sale
              </>
            )}
          </Button>

          <Button
            onClick={handleNewSaleNow}
            variant="outline"
            size="lg"
            className="w-full flex items-center justify-center gap-2"
          >
            <ArrowRight className="w-4 h-4" />
            Start New Sale Now
          </Button>
        </div>
      </div>
    </div>
  )
}